import { Injectable, NgZone } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Observable, BehaviorSubject, Subject, of, EMPTY, timer, throwError, concat, defer, from, lastValueFrom } from 'rxjs';
import { catchError, expand, filter, map, concatMap, tap, switchMap, finalize, shareReplay, timeout } from 'rxjs/operators';
import { CookieService } from 'ngx-cookie-service';
import { ToastrService } from 'ngx-toastr';
import { global } from '../global';

/** Cookie / localStorage keys used for MetaAPI credentials */
export const META_API_TOKEN_KEY = 'metaapi_token';
export const META_API_ACCOUNT_KEY = 'metaapi_account_id';
export const META_API_REGION_KEY = 'metaapi_region';
/** JSON map accountId → region chosen by hand; survives logout so the choice outlives the session */
const META_API_REGION_MANUAL_KEY = 'metaapi_region_manual';
/** Region used until the account's own region (tbl_metaapi_cuenta.region) is known */
const DEFAULT_REGION = 'backup-new-york';
/** Regions the user can switch the client API to (header selector) */
export const METAAPI_REGIONES: string[] = ['london', 'backup-new-york'];

/**
 * Where the MetaTrader account lives: MetaAPI cloud, or the local Nectum MT5 Gateway
 * (https://localhost:8444), which serves the same REST API on the user's own MT5 terminal.
 * Stored in localStorage next to the credentials; tbl_metaapi_cuenta.origen on the backend.
 */
export const META_API_ORIGEN_KEY = 'metaapi_origen';
export type MetaApiOrigen = 'CLOUD' | 'GATEWAY';
/** The gateway only requires the auth-token header to be present (it forces the CORS preflight) */
export const MT5_GATEWAY_TOKEN = 'gateway';
/**
 * MetaTrader only connects through the local gateway: the MetaAPI cloud login is hidden and a
 * cloud session left in localStorage is closed on startup. Set to true to bring the cloud back.
 */
export const METAAPI_CLOUD_HABILITADO = false;

/**
 * What placeOrder/modifyOrder/cancelOrder answer, without calling the gateway, while the MT5
 * "Algo Trading" button is off: the same rejection MT5 would give, so callers handle it as one.
 */
const ALGO_TRADING_DESACTIVADO = {
  numericCode: 10027,
  stringCode: 'TRADE_RETCODE_CLIENT_DISABLES_AT',
  message: 'Algo Trading está desactivado en MetaTrader 5'
};

/** localStorage prefix for the accountInformation cache shared across tabs */
export const META_API_ACCINFO_CACHE_PREFIX = 'metaapi_accinfo_';
/** Max cache age accepted by default (header polling runs every 10 s) */
export const ACC_INFO_TTL_MS = 10_000;
/** Max cache age accepted by /broker (its status polling runs every 5 s) */
export const ACC_INFO_TTL_BROKER_MS = 4_500;
/** Consecutive transient failures (504, 5xx, network, timeout) tolerated before reporting "disconnected" */
const MAX_TRANSIENT_FAILURES = 3;

/** localStorage prefix for the last activity ping sent to the backend, shared across tabs */
export const META_API_PING_PREFIX = 'metaapi_last_ping_';
/** Min time between activity pings (backend undeploys after metaapi.inactivity.minutes without one) */
const PING_INTERVAL_MS = 300_000;

/** accountInformation cache entry: a real response, or a shared marker of a transient failure */
type AccInfoCacheEntry = { data: any } | { failed: true };

export type MetaApiConnectionStatus = 'ready' | 'missing-credentials' | 'not-connected';

/** Which fill pass the candle download is currently running — for progress-bar labeling. */
export type MetaApiDownloadFase = 'inicial' | 'hueco' | 'cola-antigua' | 'reciente';

/** Lookback unit: 'M' months, 'A' years (años). */
export type UnidadHistorico = 'M' | 'A';

export interface PeriodoHistoricoTope {
  cantidad: number;
  unidad: UnidadHistorico;
}

/**
 * Row returned by GET fmp/periodosHistorico (weekly excluded, it isn't used): one lookback per
 * source. `fmp` is null where FMP doesn't use it; `mt` is MetaTrader via MetaAPI.
 */
export interface PeriodoHistoricoFila {
  periodo: number;
  timeframe: string;
  fmp: PeriodoHistoricoTope | null;
  mt: PeriodoHistoricoTope;
}

export interface MetaApiDownloadGuardResult {
  ok: boolean;
  status: MetaApiConnectionStatus;
  message: string;
  accountId: string;
  /** Toast title: names the actual source (MetaAPI cloud or the local MT5 gateway) */
  title: string;
}

/**
 * Thin HTTP wrapper around the MetaAPI Cloud REST API.
 * Base URL: https://mt-client-api-v1.{region}.agiliumtrade.ai — region comes from
 * tbl_metaapi_cuenta.region (DEFAULT_REGION until it is known).
 *
 * Authentication: every request must include the header `auth-token: <token>`.
 * The token and accountId are stored in localStorage after login.
 */
@Injectable({
  providedIn: 'root'
})
export class MetaApiService {

  private baseUrl = `https://mt-client-api-v1.${DEFAULT_REGION}.agiliumtrade.ai`;
  private baseUrlData = `https://mt-market-data-client-api-v1.${DEFAULT_REGION}.agiliumtrade.ai`;

  /** Region the client API base URLs currently point to. */
  private regionSubject = new BehaviorSubject<string>(DEFAULT_REGION);
  public region$ = this.regionSubject.asObservable();

  /** Emits `true` while the stored token appears valid. */
  private connectedSubject = new BehaviorSubject<boolean>(false);
  public connected$ = this.connectedSubject.asObservable();

  /** Emits when the credentials are cleared (broker/app logout). Unlike connected$, it never fires on transient failures. */
  private credentialsClearedSubject = new Subject<void>();
  public credentialsCleared$ = this.credentialsClearedSubject.asObservable();

  /** Emits the new active accountId ('' on logout) when it changes, in this tab or in another one. */
  private cuentaCambiadaSubject = new Subject<string>();
  public cuentaCambiada$ = this.cuentaCambiadaSubject.asObservable();

  /** Gateway: true while MT5 has the active account open with its "Algo Trading" button off. */
  private algoTradingOffSubject = new BehaviorSubject<boolean>(false);
  public algoTradingOff$ = this.algoTradingOffSubject.asObservable();

  private readonly backendUrl = global.url;
  private refreshing = false;

  /** In-flight shared accountInformation requests, per accountId (same-tab dedupe) */
  private accInfoInFlight = new Map<string, Observable<any>>();

  /** Especificaciones ya descargadas, por cuenta|símbolo con el case del broker (ver digitsEnCache). */
  private specsCache = new Map<string, any>();

  /** verifyConnection state: last verified result in this tab and current transient-failure streak */
  private lastKnownConnected: boolean | null = null;
  private consecutiveTransientFailures = 0;

  constructor(
    private http: HttpClient,
    private cookieService: CookieService,
    private toastr: ToastrService,
    private ngZone: NgZone
  ) {
    // MetaAPI cloud disabled: a cloud session from before is closed, only the gateway is kept
    if (!METAAPI_CLOUD_HABILITADO && localStorage.getItem(META_API_TOKEN_KEY) && !this.isGateway()) {
      this.clearCredentials();
      if (localStorage.getItem('brokerTipoBroker') === 'METAAPI') {
        localStorage.setItem('brokerConectado', 'false');
      }
    }
    // Restore connectivity state from previous session
    const saved = localStorage.getItem(META_API_TOKEN_KEY);
    if (saved) {
      this.connectedSubject.next(true);
    }
    // Base URLs for the stored origin (local gateway or MetaAPI cloud)
    this.applyRegion(DEFAULT_REGION);
    // Restore region URL from previous session; if it was never stored (manual token,
    // older sessions), resolve it from the account's row in tbl_metaapi_cuenta
    const savedRegion = localStorage.getItem(META_API_REGION_KEY);
    const manualRegion = this.getRegionManual(this.getAccountId());
    if (manualRegion) {
      this.applyRegion(manualRegion);
    } else if (savedRegion && METAAPI_REGIONES.includes(savedRegion)) {
      this.applyRegion(savedRegion);
    } else if (this.getAccountId()) {
      this.syncRegionFromCuenta(this.getAccountId()).subscribe();
    }
    // Keep this tab's base URLs in sync when another tab switches account/region/origin
    window.addEventListener('storage', (e: StorageEvent) => {
      if (e.key === META_API_REGION_KEY) {
        this.applyRegion(e.newValue || DEFAULT_REGION);
      } else if (e.key === META_API_ORIGEN_KEY) {
        this.applyRegion(this.getRegion());
      } else if (e.key === META_API_ACCOUNT_KEY && e.newValue !== e.oldValue) {
        this.ngZone.run(() => this.cuentaCambiadaSubject.next(e.newValue || ''));
      }
    });
    this.cargarPeriodosHistorico();
  }

  // ---------------------------------------------------------------------------
  // Credentials helpers
  // ---------------------------------------------------------------------------

  getToken(): string {
    return localStorage.getItem(META_API_TOKEN_KEY) || '';
  }

  /**
   * Máximo de activos de una lista para descargar sus velas de MetaAPI al elegirla (tabla del broker,
   * crear modelo, strategy factory). No aplica a los activos de la cartera suscrita del broker.
   */
  static readonly MAX_ACTIVOS_DESCARGA = 10;

  /** true si la lista supera MAX_ACTIVOS_DESCARGA; en ese caso avisa del motivo de no descargar. */
  avisarSiExcedeLimiteDescarga(totalActivos: number, nombreLista?: string): boolean {
    if (totalActivos <= MetaApiService.MAX_ACTIVOS_DESCARGA) return false;
    const lista = nombreLista ? `La lista ${nombreLista}` : 'La lista seleccionada';
    this.toastr.warning(
      `${lista} tiene ${totalActivos} activos. Solo se descargan velas de MetaAPI para listas de ` +
      `${MetaApiService.MAX_ACTIVOS_DESCARGA} activos o menos: elige o crea una lista más pequeña.`,
      'Descarga no realizada',
      { timeOut: 8000 }
    );
    return true;
  }

  /** MetaAPI answers 401 to any request without auth-token */
  hasToken(): boolean {
    return !!this.getToken();
  }

  getAccountId(): string {
    return localStorage.getItem(META_API_ACCOUNT_KEY) || '';
  }

  saveCredentials(token: string, accountId: string): void {
    const anterior = this.getAccountId();
    localStorage.setItem(META_API_TOKEN_KEY, token);
    localStorage.setItem(META_API_ACCOUNT_KEY, accountId);
    this.connectedSubject.next(true);
    if (accountId !== anterior) this.cuentaCambiadaSubject.next(accountId);
  }

  /** True when the active account goes through the local Nectum MT5 Gateway instead of MetaAPI cloud. */
  isGateway(): boolean {
    return localStorage.getItem(META_API_ORIGEN_KEY) === 'GATEWAY';
  }

  /** Points the client API to MetaAPI cloud or to the local gateway (same paths and JSON). */
  setOrigen(origen: MetaApiOrigen): void {
    if (origen === 'GATEWAY') {
      localStorage.setItem(META_API_ORIGEN_KEY, origen);
    } else {
      localStorage.removeItem(META_API_ORIGEN_KEY);
    }
    this.applyRegion(this.getRegion());
  }

  clearCredentials(): void {
    const habiaCuenta = !!this.getAccountId();
    localStorage.removeItem(META_API_TOKEN_KEY);
    localStorage.removeItem(META_API_ACCOUNT_KEY);
    localStorage.removeItem(META_API_REGION_KEY);
    localStorage.removeItem(META_API_ORIGEN_KEY);
    this.applyRegion(this.getRegion());
    this.clearAccInfoCache();
    this.algoTradingOffSubject.next(false);
    this.connectedSubject.next(false);
    this.credentialsClearedSubject.next();
    if (habiaCuenta) this.cuentaCambiadaSubject.next('');
  }

  /**
   * Gateway only: reads GET /gateway/status, updates algoTradingOff$ and emits whether orders can
   * be sent (MT5 rejects every trade request while "Algo Trading" is off). Another account open in
   * the terminal or an unreachable gateway don't count as off: the gateway itself rejects those.
   */
  comprobarAlgoTrading(): Observable<boolean> {
    const accountId = this.getAccountId();
    if (!this.isGateway() || !accountId) {
      this.algoTradingOffSubject.next(false);
      return of(true);
    }
    return this.http.get<any>(`${global.session.mt5GwBaseUrl}/gateway/status`, { headers: this.headers() }).pipe(
      timeout(10_000),
      map(estado => {
        const off = !!estado?.terminalConnected && estado.accountId === accountId && estado.algoTradingEnabled === false;
        if (this.algoTradingOffSubject.value !== off) this.algoTradingOffSubject.next(off);
        return !off;
      }),
      catchError(() => of(true))
    );
  }

  /**
   * For buttons that start a trade flow: comprobarAlgoTrading plus a warning toast when it's off, so
   * the flow stops before cancelling SL/TP, writing to the DB or queueing anything.
   */
  verificarAlgoTradingParaOperar(): Observable<boolean> {
    return this.comprobarAlgoTrading().pipe(
      tap(activo => {
        if (!activo) {
          this.toastr.warning('Algo Trading está desactivado en MetaTrader 5: actívalo en el terminal para enviar órdenes',
            'Algo Trading desactivado', { timeOut: 10000 });
        }
      })
    );
  }

  /** Sends a /trade request unless the gateway's terminal has "Algo Trading" off (checked right before). */
  private siAlgoTradingActivo(peticion: () => Observable<any>): Observable<any> {
    if (!this.isGateway()) return this.enTurnoTrade(peticion);
    return this.enTurnoTrade(() => this.comprobarAlgoTrading().pipe(
      switchMap(activo => activo ? peticion() : of({ ...ALGO_TRADING_DESACTIVADO }))
    ));
  }

  /** Mínimo entre el fin de un /trade y el inicio del siguiente. */
  private static readonly ESPACIO_TRADE_MS = 250;
  /**
   * Un /trade colgado no bloquea el turno para siempre: pasado este tiempo el siguiente sale
   * igualmente (la petición anterior no se cancela, porque la orden pudo ejecutarse).
   */
  private static readonly ESPERA_MAX_TURNO_MS = 30000;
  /** Se resuelve cuando termina el último /trade encolado. */
  private turnoTrade: Promise<void> = Promise.resolve();
  private finUltimoTrade = 0;

  /**
   * Turno global para todas las llamadas a /trade (placeOrder, modifyOrder, cancelOrder, cierres,
   * SL/TP...): una a la vez y con ESPACIO_TRADE_MS entre envíos, vengan de la cola automática o de
   * acciones manuales. Solo aplica a MetaTrader; IBKR no pasa por este servicio.
   */
  private enTurnoTrade(peticion: () => Observable<any>): Observable<any> {
    return defer(() => {
      let liberar!: () => void;
      const turnoPrevio = this.turnoTrade;
      this.turnoTrade = new Promise<void>(resolve => liberar = resolve);
      const esperaPrevio = Promise.race([turnoPrevio, new Promise<void>(r => setTimeout(r, MetaApiService.ESPERA_MAX_TURNO_MS))]);
      let enviado = false;
      return from(esperaPrevio).pipe(
        concatMap(() => timer(Math.max(0, this.finUltimoTrade + MetaApiService.ESPACIO_TRADE_MS - Date.now()))),
        concatMap(() => { enviado = true; return peticion(); }),
        finalize(() => {
          if (enviado) this.finUltimoTrade = Date.now();
          liberar();
        })
      );
    });
  }

  /**
   * Checks whether the current MetaAPI credentials can be used to download candles.
   * If the token/account is missing, it fails immediately.
   * If credentials exist, it verifies the account connection before allowing downloads.
   * When the download is allowed it also reloads the lookback per timeframe first: every sync
   * goes through here, and otherwise a tab opened before an admin change (or a sync that starts
   * before the initial load returns) would compute historicalFromDate with a stale floor.
   */
  canDownloadCandles(accountId?: string): Observable<MetaApiDownloadGuardResult> {
    return this.verificarDescarga(accountId).pipe(
      concatMap(guard => guard.ok
        ? this.refrescarPeriodosHistorico().pipe(map(() => guard))
        : of(guard))
    );
  }

  private verificarDescarga(accountId?: string): Observable<MetaApiDownloadGuardResult> {
    const resolvedAccountId = (accountId || this.getAccountId() || '').trim();
    const gateway = this.isGateway();
    const title = gateway ? 'Gateway MT5 no disponible' : 'MetaAPI no configurado';
    if (!this.getToken() || !resolvedAccountId) {
      return of({
        ok: false,
        status: 'missing-credentials',
        accountId: resolvedAccountId,
        title,
        message: gateway
          ? 'Conecta tu cuenta de MetaTrader 5 (Gateway local) antes de descargar datos.'
          : 'Configure las credenciales MetaAPI (token y cuenta) antes de descargar datos.'
      });
    }

    const noConectado = (message: string): MetaApiDownloadGuardResult =>
      ({ ok: false, status: 'not-connected', accountId: resolvedAccountId, title, message });
    const motivo$ = (): Observable<MetaApiDownloadGuardResult> => gateway
      ? this.motivoGatewayNoDisponible(resolvedAccountId).pipe(map(noConectado))
      : of(noConectado('No hay conexión activa con MetaAPI. Inicia sesión nuevamente para descargar datos.'));

    return this.verifyConnection(resolvedAccountId).pipe(
      concatMap((ok: boolean) => ok
        ? of<MetaApiDownloadGuardResult>({ ok: true, status: 'ready', accountId: resolvedAccountId, title, message: '' })
        : motivo$()),
      catchError(() => motivo$())
    );
  }

  /**
   * Why the local gateway can't serve this account, from its GET /gateway/status: gateway not
   * running, MT5 without a connected account, or another account open in the terminal (the gateway
   * answers 404 to any account that isn't the open one, so it never trades on the wrong account).
   */
  private motivoGatewayNoDisponible(accountId: string): Observable<string> {
    return this.http.get<any>(`${global.session.mt5GwBaseUrl}/gateway/status`, { headers: this.headers() }).pipe(
      timeout(15_000),
      map(estado => {
        if (!estado?.initialized) {
          return 'El Gateway MT5 no encuentra MetaTrader 5. Abre el terminal de tu bróker.';
        }
        if (!estado.terminalConnected || !estado.accountId) {
          return 'MetaTrader 5 no tiene ninguna cuenta conectada. Inicia sesión en el terminal.';
        }
        if (estado.accountId !== accountId) {
          return `MetaTrader 5 tiene abierta la cuenta ${estado.login} (${estado.server}), no la de esta cartera. `
            + 'Ábrela en el terminal o elige su terminal en el diálogo del bróker (Gateway local).';
        }
        return 'El Gateway MT5 no respondió a tiempo. Inténtalo de nuevo en unos segundos.';
      }),
      catchError(() => of('No se pudo conectar con el Gateway MT5 local (https://localhost:8444). '
        + 'Comprueba que Nectum MT5 Gateway esté en ejecución.'))
    );
  }

  /**
   * Sets the base URLs to the correct region for this account and persists it.
   * An empty region resets to the default, so a previous account's region doesn't linger.
   */
  setRegionUrl(region: string | null | undefined): void {
    if (!region) {
      this.applyRegion(DEFAULT_REGION);
      localStorage.removeItem(META_API_REGION_KEY);
      return;
    }
    this.applyRegion(region);
    localStorage.setItem(META_API_REGION_KEY, region);
  }

  /**
   * Looks up the account in tbl_metaapi_cuenta (metaapi/cuentas-guardadas) and applies its
   * region. Emits the region found, or null when the account isn't registered (manual token
   * of an account outside the app) or the lookup fails — the current region is kept then.
   */
  /** Region picked by hand for this account (header/login selector); beats the one stored in the DB. */
  getRegionManual(accountId: string): string | null {
    try {
      const map = JSON.parse(localStorage.getItem(META_API_REGION_MANUAL_KEY) || '{}');
      const region = map?.[accountId];
      return METAAPI_REGIONES.includes(region) ? region : null;
    } catch {
      return null;
    }
  }

  /** Applies a region chosen by the user and remembers it for the active account. */
  setRegionManual(region: string): void {
    const accountId = this.getAccountId();
    if (accountId) {
      try {
        const map = JSON.parse(localStorage.getItem(META_API_REGION_MANUAL_KEY) || '{}');
        map[accountId] = region;
        localStorage.setItem(META_API_REGION_MANUAL_KEY, JSON.stringify(map));
      } catch { /* storage unavailable: the choice only lasts this session */ }
    }
    this.setRegionUrl(region);
  }

  /** Region to use for an account: the manual choice if any, else the one from the DB. */
  resolveRegion(accountId: string, regionCuenta: string | null | undefined): string | null | undefined {
    return this.getRegionManual(accountId) || regionCuenta;
  }

  syncRegionFromCuenta(accountId: string): Observable<string | null> {
    const licencia = this.cookieService.get('auth_token');
    if (!licencia || !accountId) return of(null);
    return this.http.get<any>(
      `${this.backendUrl}metaapi/cuentas-guardadas`,
      { headers: new HttpHeaders({ 'licencia': licencia, 'Content-Type': 'application/json' }) }
    ).pipe(
      map(data => {
        const cuenta = (data?.cuentas || []).find((c: any) => c?.metaApiAccountId === accountId);
        const region: string | null = this.resolveRegion(accountId, cuenta?.region) || null;
        if (region) this.setRegionUrl(region);
        return region;
      }),
      catchError(() => of(null))
    );
  }

  getRegion(): string {
    return this.regionSubject.value;
  }

  private applyRegion(region: string): void {
    if (this.isGateway()) {
      // The gateway serves the client and market-data APIs on the same origin
      this.baseUrl = global.session.mt5GwBaseUrl;
      this.baseUrlData = global.session.mt5GwBaseUrl;
    } else {
      this.baseUrl = `https://mt-client-api-v1.${region}.agiliumtrade.ai`;
      this.baseUrlData = `https://mt-market-data-client-api-v1.${region}.agiliumtrade.ai`;
    }
    this.regionSubject.next(region);
  }

  // ---------------------------------------------------------------------------
  // Private helpers
  // ---------------------------------------------------------------------------

  private headers(): HttpHeaders {
    return new HttpHeaders({
      'auth-token': this.getToken(),
      'Content-Type': 'application/json'
    });
  }

  private url(path: string): string {
    return `${this.baseUrl}${path}`;
  }

  private urlData(path: string): string {
    return `${this.baseUrlData}${path}`;
  }

  // ---------------------------------------------------------------------------
  // Account
  // ---------------------------------------------------------------------------

  /**
   * GET /users/current/accounts/{accountId}/accountInformation
   * Returns balance, equity, margin, freeMargin, credit, etc.
   *
   * @param throwOnTransient when true, transient failures (504, 5xx, network,
   *        429) are thrown instead of being mapped to `null`.
   */
  getAccountInformation(accountId: string, throwOnTransient = false): Observable<any> {
    const info$ = this.callWithAutoRefresh(
      accountId,
      () => this.http.get(
        this.url(`/users/current/accounts/${accountId}/accountInformation`),
        { headers: this.headers() }
      ),
      null,
      err => this.isTransientError(err)
    ).pipe(
      // Only real responses feed the cache shared with the other tabs and keep the account alive
      tap(data => {
        if (data != null) {
          this.writeAccInfoCache(accountId, { data });
          this.pingActividad(accountId);
        }
      })
    );
    return throwOnTransient ? info$ : info$.pipe(catchError(() => of(null)));
  }

  /**
   * Same as getAccountInformation, but shared across browser tabs: returns the
   * cached response if it is younger than `maxAgeMs`; otherwise only one tab
   * (the one that gets the Web Lock) hits MetaAPI and the rest reuse its result.
   */
  getAccountInformationShared(accountId: string, maxAgeMs: number = ACC_INFO_TTL_MS): Observable<any> {
    return this.fetchAccountInformationShared(accountId, maxAgeMs).pipe(catchError(() => of(null)));
  }

  /** Shared variant that propagates transient failures (used by verifyConnection). */
  private fetchAccountInformationShared(accountId: string, maxAgeMs: number): Observable<any> {
    const cached = this.readAccInfoCache(accountId, maxAgeMs);
    if (cached) {
      return this.fromAccInfoCache(cached);
    }

    let inFlight = this.accInfoInFlight.get(accountId);
    if (!inFlight) {
      inFlight = this.withCrossTabLock(`metaapi-accinfo-${accountId}`, () => {
        // Another tab may have refreshed the cache while we waited for the lock
        const again = this.readAccInfoCache(accountId, maxAgeMs);
        if (again) {
          return this.fromAccInfoCache(again);
        }
        return this.getAccountInformation(accountId, true).pipe(
          timeout(15_000),
          catchError(err => {
            // Share the failure so the other tabs don't all retry at once
            if (this.isTransientError(err)) {
              this.writeAccInfoCache(accountId, { failed: true });
            }
            return throwError(() => err);
          })
        );
      }).pipe(
        finalize(() => this.accInfoInFlight.delete(accountId)),
        shareReplay({ bufferSize: 1, refCount: false })
      );
      this.accInfoInFlight.set(accountId, inFlight);
    }
    return inFlight;
  }

  private fromAccInfoCache(entry: AccInfoCacheEntry): Observable<any> {
    return 'failed' in entry
      ? throwError(() => ({ metaApiTransient: true }))
      : of(entry.data);
  }

  private readAccInfoCache(accountId: string, maxAgeMs: number): AccInfoCacheEntry | undefined {
    try {
      const raw = localStorage.getItem(META_API_ACCINFO_CACHE_PREFIX + accountId);
      if (!raw) return undefined;
      const entry = JSON.parse(raw);
      if (typeof entry?.ts !== 'number' || Date.now() - entry.ts >= maxAgeMs) return undefined;
      return entry.failed ? { failed: true } : { data: entry.data ?? null };
    } catch {
      return undefined;
    }
  }

  private writeAccInfoCache(accountId: string, entry: AccInfoCacheEntry): void {
    try {
      localStorage.setItem(
        META_API_ACCINFO_CACHE_PREFIX + accountId,
        JSON.stringify({ ts: Date.now(), ...entry })
      );
    } catch { /* storage full or blocked — cache is best-effort */ }
  }

  /**
   * True for failures that don't prove the account is disconnected: rxjs
   * timeout, a shared failure marker, network/CORS (status 0), 429 or 5xx —
   * including MetaAPI's 504 TimeoutError while the terminal is reconnecting.
   */
  isTransientError(err: any): boolean {
    if (err?.name === 'TimeoutError' || err?.metaApiTransient === true) return true;
    const status = err?.status;
    return status === 0 || status === 429 || (status >= 500 && status <= 599);
  }

  private clearAccInfoCache(): void {
    try {
      Object.keys(localStorage)
        .filter(key => key.startsWith(META_API_ACCINFO_CACHE_PREFIX) || key.startsWith(META_API_PING_PREFIX))
        .forEach(key => localStorage.removeItem(key));
    } catch { /* ignore */ }
  }

  /**
   * Refreshes last_active in the backend so the inactivity scheduler doesn't
   * undeploy the account while the app is in use. Throttled to one ping every
   * PING_INTERVAL_MS across all tabs.
   */
  private pingActividad(accountId: string): void {
    const key = META_API_PING_PREFIX + accountId;
    try {
      const last = Number(localStorage.getItem(key));
      if (last && Date.now() - last < PING_INTERVAL_MS) return;
      localStorage.setItem(key, String(Date.now()));
    } catch { /* storage blocked — ping without throttle */ }

    const licencia = this.cookieService.get('auth_token');
    if (!licencia) return;
    this.http.post(
      `${this.backendUrl}metaapi/ping/${accountId}`,
      {},
      { headers: new HttpHeaders({ 'licencia': licencia, 'Content-Type': 'application/json' }) }
    ).subscribe({
      error: () => {
        // Retry on the next accountInformation response
        try { localStorage.removeItem(key); } catch { /* ignore */ }
      }
    });
  }

  /**
   * Runs `work` while holding a Web Lock shared by all tabs of this origin, so
   * only one tab at a time executes it. The browser releases the lock if the
   * tab closes. Falls back to running without coordination if unsupported.
   */
  private withCrossTabLock<T>(name: string, work: () => Observable<T>): Observable<T> {
    const locks = (navigator as any)?.locks;
    if (!locks?.request) {
      return work();
    }
    return new Observable<T>(subscriber => {
      (locks.request(name, () => lastValueFrom(work(), { defaultValue: null })) as Promise<T>).then(
        value => this.ngZone.run(() => { subscriber.next(value); subscriber.complete(); }),
        err => this.ngZone.run(() => subscriber.error(err))
      );
    });
  }



  // ---------------------------------------------------------------------------
  // Positions
  // ---------------------------------------------------------------------------

  /**
   * GET /users/current/accounts/{accountId}/positions
   * Returns all currently open positions.
   */
  getPositions(accountId: string): Observable<any[]> {
    return this.callWithAutoRefresh(
      accountId,
      () => this.http.get<any[]>(
        this.url(`/users/current/accounts/${accountId}/positions`),
        { headers: this.headers() }
      ),
      []
    );
  }

  // ---------------------------------------------------------------------------
  // Orders
  // ---------------------------------------------------------------------------

  /**
   * GET /users/current/accounts/{accountId}/orders
   * Returns all pending (not yet filled) orders.
   */
  getOrders(accountId: string): Observable<any[]> {
    if (!this.hasToken()) return of([]);
    const request = () => this.http.get<any[]>(
      this.url(`/users/current/accounts/${accountId}/orders`),
      { headers: this.headers() }
    );
    return request().pipe(
      catchError((err: HttpErrorResponse) => {
        if (err?.error?.error === 'TooManyRequestsError') {
          return throwError(() => err);
        }
        // The local gateway has no token to renew
        if (err.status === 401 && !this.refreshing && !this.isGateway()) {
          this.refreshing = true;
          return this.doRefreshToken(accountId).pipe(
            switchMap(() => { this.refreshing = false; return request(); }),
            catchError(() => { this.refreshing = false; return of([]); })
          );
        }
        console.error('[MetaApiService] getOrders error', err);
        return of([]);
      })
    );
  }

  /**
   * GET /users/current/accounts/{accountId}/symbols
   * Returns all symbols (instruments) available for trading on the account.
   * Each entry includes: name, description, digits, contractSize, minVolume, etc.
   */
  getSymbols(accountId: string): Observable<any[]> {
    if (!this.hasToken()) return of([]);
    return this.http.get<any[]>(
      this.url(`/users/current/accounts/${accountId}/symbols`),
      { headers: this.headers() }
    ).pipe(
      catchError(err => {
        console.error('[MetaApiService] getSymbols error', err);
        return of([]);
      })
    );
  }

  /**
   * GET /users/current/accounts/{accountId}/symbols/{symbol}/specification
   * Returns symbol specification: digits, contractSize, minVolume, maxVolume, volumeStep, etc.
   */
  getSymbolSpecification(accountId: string, symbol: string): Observable<any> {
    if (!this.hasToken()) return of(null);
    return this.http.get(
      this.url(`/users/current/accounts/${accountId}/symbols/${encodeURIComponent(symbol)}/specification`),
      { headers: this.headers() }
    ).pipe(
      tap((spec: any) => { if (spec) this.specsCache.set(`${accountId}|${symbol}`, spec); }),
      catchError(err => {
        console.error('[MetaApiService] getSymbolSpecification error', err);
        return of(null);
      })
    );
  }

  /**
   * Decimales del precio (spec.digits) de un símbolo cuya especificación ya se descargó con
   * getSymbolSpecification; null si no está en caché. Un SL/TP con más decimales de los que
   * admite el símbolo (XAGUSD.sa: 3) lo rechaza el broker.
   */
  digitsEnCache(accountId: string, symbol: string): number | null {
    const digits = Number(this.specsCache.get(`${accountId}|${symbol}`)?.digits);
    return Number.isInteger(digits) && digits >= 0 ? digits : null;
  }

  /**
   * GET /users/current/accounts/{accountId}/symbols/{symbol}/current-price?keepSubscription=true
   * Returns the current bid/ask price for a symbol.
   */
  getCurrentPrice(accountId: string, symbol: string): Observable<any> {
    //console.log(`[MetaApiService] Fetching current price for ${symbol}...`);
    if (!this.hasToken()) return of(null);
    return this.http.get(
      this.url(`/users/current/accounts/${accountId}/symbols/${encodeURIComponent(symbol)}/current-price?keepSubscription=true`),
      { headers: this.headers() }
    ).pipe(
      catchError(err => {
        console.error('[MetaApiService] getCurrentPrice error', err);
        return of(null);
      })
    );
  }

  /**
   * GET /users/current/accounts/{accountId}/symbols/{symbol}/current-tick?keepSubscription=true
   * Returns the latest tick (bid, ask, last) for a symbol.
   */
  getCurrentTick(accountId: string, symbol: string): Observable<any> {
    if (!this.hasToken()) return of(null);
    return this.http.get(
      this.url(`/users/current/accounts/${accountId}/symbols/${encodeURIComponent(symbol)}/current-tick?keepSubscription=true`),
      { headers: this.headers() }
    ).pipe(
      catchError(err => {
        console.error('[MetaApiService] getCurrentTick error', err);
        return of(null);
      })
    );
  }

  /**
   * POST /users/current/accounts/{accountId}/trade
   * Places a new order. `trade` must match the MetaAPI TradeRequest schema:
   * { actionType, symbol, volume, openPrice?, stopLoss?, takeProfit?, comment? }
   */
  placeOrder(accountId: string, trade: any): Observable<any> {
    if (!this.hasToken()) return of({ error: { message: 'Sin token MetaAPI' } });
    return this.siAlgoTradingActivo(() => this.http.post(
      this.url(`/users/current/accounts/${accountId}/trade`),
      trade,
      { headers: this.headers() }
    ).pipe(
      catchError(err => {
        console.error('[MetaApiService] placeOrder error', err);
        return of({ error: err });
      })
    ));
  }

  /**
   * PUT /users/current/accounts/{accountId}/trade
   * Modifies an existing pending order using ORDER_MODIFY actionType.
   * { actionType: 'ORDER_MODIFY', orderId, openPrice?, stopLoss?, takeProfit? }
   */
  modifyOrder(accountId: string, orderId: string, changes: { openPrice?: number; stopLoss?: number; takeProfit?: number }): Observable<any> {
    if (!this.hasToken()) return of({ error: { message: 'Sin token MetaAPI' } });
    const trade = { actionType: 'ORDER_MODIFY', orderId: String(orderId), ...changes };
    return this.siAlgoTradingActivo(() => this.http.post(
      this.url(`/users/current/accounts/${accountId}/trade`),
      trade,
      { headers: this.headers() }
    ).pipe(
      catchError(err => {
        console.error('[MetaApiService] modifyOrder error', err);
        return of({ error: err });
      })
    ));
  }

  /**
   * POST /users/current/accounts/{accountId}/trade
   * Cancels a pending order via actionType ORDER_CANCEL.
   */
  cancelOrder(accountId: string, orderId: string): Observable<any> {
    if (!this.hasToken()) return of({ error: { message: 'Sin token MetaAPI' } });
    const trade = { actionType: 'ORDER_CANCEL', orderId: String(orderId) };
    return this.siAlgoTradingActivo(() => this.http.post(
      this.url(`/users/current/accounts/${accountId}/trade`),
      trade,
      { headers: this.headers() }
    ).pipe(
      catchError(err => {
        console.error('[MetaApiService] cancelOrder error', err);
        return of({ error: err });
      })
    ));
  }

  /**
   * GET /users/current/accounts/{accountId}/history-deals/time/{startTime}/{endTime}
   * Returns closed deals in a time range (ISO-8601 strings).
   * Each deal includes: symbol, positionId, profit, volume, type (DEAL_TYPE_BUY/SELL), time, etc.
   * Use this to check the profit/loss of a recently closed position.
   */
  getHistoryDeals(accountId: string, startTime: string, endTime: string): Observable<any[]> {
    if (!this.hasToken()) return of([]);
    return this.http.get<any[]>(
      this.url(`/users/current/accounts/${accountId}/history-deals/time/${encodeURIComponent(startTime)}/${encodeURIComponent(endTime)}`),
      { headers: this.headers() }
    ).pipe(
      catchError(err => {
        // Propagate instead of returning []: the caller treats an empty list as "no new closes",
        // so swallowing a 429 or a network cut here was indistinguishable from a quiet market and
        // the missed closes were never retried.
        console.error('[MetaApiService] getHistoryDeals error', err);
        return throwError(() => err);
      })
    );
  }

  /**
   * GET /users/current/accounts/{accountId}/historical-market-data/symbols/{symbol}/timeframes/{timeframe}/candles
   * Returns historical OHLCV candles for a symbol and timeframe.
   * @param timeframe e.g. '1m', '5m', '15m', '30m', '1h', '4h', '1d', '1w', '1mn'
   * @param startTime ISO-8601 start date (optional)
   * @param endTime   ISO-8601 end date   (optional)
   * @param count     Maximum number of candles to return (optional)
   */
  getCandles(
    accountId: string,
    symbol: string,
    timeframe: string,
    startTime?: string,
    limit?: string
  ): Observable<any[]> {
    const params: Record<string, string> = {};
    if (startTime) params['startTime'] = startTime;
   // if (endTime)   params['endTime']   = endTime;
    if (limit != null) params['limit'] = limit;
    const query = Object.keys(params).length
      ? '?' + new URLSearchParams(params).toString()
      : '';
    if (!this.hasToken()) return of([]);
    return this.http.get<any[]>(
      this.urlData(`/users/current/accounts/${accountId}/historical-market-data/symbols/${encodeURIComponent(symbol)}/timeframes/${encodeURIComponent(timeframe)}/candles${query}`),
      { headers: this.headers() }
    ).pipe(
      catchError(err => {
        console.error('[MetaApiService] getCandles error', err);
        return of([]);
      })
    );
  }

  // ---------------------------------------------------------------------------
  // Historical candles with automatic pagination
  // ---------------------------------------------------------------------------

  /**
   * MetaTrader lookback per timeframe, loaded from the backend (tbl_periodo_historico,
   * fuente 'MT', editable in the admin panel). Null until the first load succeeds;
   * historicalFromDate falls back to the built-in profile below meanwhile.
   */
  private static topesHistorico: Record<string, PeriodoHistoricoTope> | null = null;

  /** Loads the MetaTrader lookback per timeframe from the backend into the static cache. */
  cargarPeriodosHistorico(): void {
    this.refrescarPeriodosHistorico().subscribe();
  }

  /**
   * Same as cargarPeriodosHistorico, but completes once the cache is updated (or the request
   * failed, keeping whatever was loaded before). Never errors.
   */
  refrescarPeriodosHistorico(): Observable<void> {
    return this.http.get<PeriodoHistoricoFila[]>(`${this.backendUrl}fmp/periodosHistorico`).pipe(
      map((filas) => {
        if (!Array.isArray(filas)) return;
        const mapa: Record<string, PeriodoHistoricoTope> = {};
        filas.forEach(f => {
          const mt = f?.mt;
          if (f?.timeframe && mt && mt.cantidad > 0 && ['M', 'A'].includes(mt.unidad)) {
            mapa[f.timeframe] = { cantidad: mt.cantidad, unidad: mt.unidad };
          }
        });
        MetaApiService.topesHistorico = mapa;
      }),
      catchError((err) => {
        console.warn('[MetaApi] periodosHistorico no disponible, se usa el último tope cargado o el perfil por defecto', err);
        return of(undefined);
      })
    );
  }

  /** Returns the lookback start-date for a given timeframe. */
  static historicalFromDate(timeframe: string): Date {
    const d = new Date();
    const tope = MetaApiService.topesHistorico?.[timeframe];
    if (tope) {
      if (tope.unidad === 'A') d.setFullYear(d.getFullYear() - tope.cantidad);
      else d.setMonth(d.getMonth() - tope.cantidad);
      return d;
    }
    // Fallback when the backend config hasn't loaded: mirrors PeriodoEnum.mesesHistorico.
    const monthsBack: Record<string, number> = {
      '1m': 6, '5m': 12, '15m': 60, '30m': 96, '1h': 120, '4h': 144, '1d': 180, '1w': 180,
    };
    d.setMonth(d.getMonth() - (monthsBack[timeframe] ?? 60));
    return d;
  }

  private timeframeMs(timeframe: string): number {
    const map: Record<string, number> = {
      '1m': 60_000, '5m': 300_000, '15m': 900_000, '30m': 1_800_000,
      '1h': 3_600_000, '4h': 14_400_000, '1d': 86_400_000,
      '1w': 604_800_000, '1mn': 2_592_000_000
    };
    return map[timeframe] ?? 86_400_000;
  }

  candleComparisonTime(candle: any, timeframe: string): number | null {
    const isIntraday = timeframe !== '1d' && timeframe !== '1w' && timeframe !== '1mn';
    const brokerTime = candle?.brokerTime;

    // Prefer brokerTime when available (more accurate broker-side timestamp).
    if (typeof brokerTime === 'string' && brokerTime.length >= 8) {
      const normalized = brokerTime.includes('T') ? brokerTime : brokerTime.replace(' ', 'T');
      // Append 'Z' if timezone is missing to parse as UTC (keeps previous behavior)
      const parsed = new Date(normalized + 'Z');
      if (!Number.isNaN(parsed.getTime())) {
        return parsed.getTime();
      }
    }

    // Fallback to generic `time` field if brokerTime not present or unparseable.
    const time = candle?.time;
    if (typeof time !== 'string') return null;
    const parsed = new Date(time);
    return Number.isNaN(parsed.getTime()) ? null : parsed.getTime();
  }

  /** Instante UTC real de la vela (campo `time`), el que MetaAPI espera en startTime. */
  private candleRealTime(candle: any): number | null {
    const time = candle?.time;
    if (typeof time !== 'string') return null;
    const ms = new Date(time).getTime();
    return Number.isNaN(ms) ? null : ms;
  }

  /**
  * Fetches historical candles with automatic pagination (max 1000 per request).
   * Emits one array per batch so the caller can persist each batch immediately.
   * Stops when data older than fromDate is reached or the API returns no more data.
   * Adds a 300 ms delay between requests to avoid rate-limiting.
  * Supports '1m' timeframe via pagination; last-candle optimization applies to '1d' and '1m'.
   */
  getCandlesHistorical(
    accountId: string,
    symbol: string,
    timeframe: string,
    fromDate: Date,
    // Optional optimization: last known candle date (e.g. '2026-06-03') and current date
    // If provided for daily timeframe we will request only the missing days instead of defaulting to PAGE=1000
    lastCandleDate?: string,
    currentDate?: string,
    onBatch?: (batchIndex: number, totalCandles: number) => void,
    onTotal?: (expectedCandles: number) => void,
    // Called once, only when MetaApi runs out of data before reaching the requested `fromDate`
    // (as opposed to successfully reaching it) — signals the broker's true history floor.
    onExhausted?: (earliestConfirmedDate: string) => void
  ): Observable<any[]> {
    const PAGE = 1000;
    // Safety cap on pagination. Without it, a broker that keeps answering with very short pages
    // would keep the walk going indefinitely; 1m over its 180-day floor needs ~260 full pages.
    const MAX_PAGES = 2000;
    const tfMs = this.timeframeMs(timeframe);
    let batchIndex = 0;
    let totalCandles = 0;
    let pageCount = 0;

    const fetchPage = (cursor: string | null, pageLimit: number = PAGE): Observable<{ candles: any[]; cursor: string | null; done: boolean }> => {
      if (!this.hasToken()) return of({ candles: [], cursor: null, done: true });
      const params: Record<string, string> = { limit: String(pageLimit) };
      if (cursor) params['startTime'] = cursor;
      const query = '?' + new URLSearchParams(params).toString();
      pageCount++;
      return this.http.get<any[]>(
        this.urlData(`/users/current/accounts/${accountId}/historical-market-data/symbols/${encodeURIComponent(symbol)}/timeframes/${encodeURIComponent(timeframe)}/candles${query}`),
        { headers: this.headers() }
      ).pipe(
        map((raw: any[]) => {
          if (!raw || raw.length === 0) {
            // No data at all at/before the cursor: broker has nothing older than this point.
            if (cursor) onExhausted?.(cursor);
            return { candles: [], cursor: null, done: true };
          }
          const items = raw
            .map(c => ({ c, t: this.candleComparisonTime(c, timeframe) }))
            .filter((item): item is { c: any; t: number } => item.t != null);
          if (items.length === 0) return { candles: [], cursor: null, done: true };
          const times = items.map(i => i.t);
          const oldestMs = Math.min(...times);
          // El cursor se manda a MetaAPI, que lee startTime como UTC real: sale de `time`, no de
          // brokerTime. Con brokerTime (UTC+N tratado como UTC) cerca de "ahora" el cursor caía en
          // el futuro, MetaAPI respondía siempre la última vela y la paginación no retrocedía.
          const nextCursorMs = Math.min(...items.map(i => this.candleRealTime(i.c) ?? i.t)) - tfMs;
          const nextCursor = new Date(nextCursorMs).toISOString();
          const reachedFloor = new Date(oldestMs) <= fromDate;
          // Red de seguridad: si el cursor no retrocede, la siguiente página sería la misma.
          // El cursor llega como ISO con Z (toISOString) o como la fechaActual del servidor sin zona.
          const cursorMs = cursor ? new Date(/Z$|[+-]\d\d:\d\d$/.test(cursor) ? cursor : cursor.replace(' ', 'T') + 'Z').getTime() : NaN;
          if (!Number.isNaN(cursorMs) && nextCursorMs >= cursorMs) {
            console.warn(`[MetaApiService] getCandlesHistorical: el cursor no retrocede (${cursor} → ${nextCursor}), se corta la paginación`);
            items.sort((a, b) => a.t - b.t);
            return { candles: (pageLimit ? items.slice(-pageLimit) : items).map(i => i.c), cursor: null, done: true };
          }
          // A short page is NOT proof that the broker ran out of history: MetaApi also returns
          // fewer rows than asked when it clips the response to its own internal time window.
          // Treating it as the end used to stop the walk early AND persist `historiaAgotadaDesde`
          // permanently, closing the old-tail backfill for good. Keep paginating on a short page:
          // the walk still terminates, either at the floor or at the empty page handled above,
          // which is the only real proof that history is exhausted.
          const done = reachedFloor || pageCount >= MAX_PAGES;
          // Sort by time ascending and return the last `pageLimit` records (most recent)
          items.sort((a, b) => a.t - b.t);
          const sliced = pageLimit ? items.slice(-pageLimit) : items;
          const candles = sliced.map(i => i.c);
          return { candles, cursor: nextCursor, done };
        }),
        catchError((err: HttpErrorResponse) => {
          // Distinguish a real transport/HTTP failure (network cut, timeout, 5xx) from a
          // legitimate "no more data" response — swallowing the former as done:true made
          // interrupted downloads look like successful completions (silent data gaps).
          console.error('[MetaApiService] getCandlesHistorical fetchPage error', err);
          return throwError(() => err);
        })
      );
    };

    // When lastCandleDate is available (gap-fill scenario), use fechaActual as the anchor
    // (startTime) for all timeframes. MetaAPI returns `limit` candles backwards from startTime.
    // Paginate until the oldest fetched candle reaches ultimaFecha (fromDate).
    if (lastCandleDate) {
      try {
        const last = new Date(lastCandleDate);
        const now = currentDate ? new Date(currentDate) : new Date();
        if (!Number.isNaN(last.getTime()) && !Number.isNaN(now.getTime())) {
          const diffMs = now.getTime() - last.getTime();
          // Number of candles missing between ultimaFecha and fechaActual
          const missing = Math.ceil(diffMs / tfMs) - 1;
          if (missing <= 0) {
            return of([]);
          }
          // Anchor: startTime = fechaActual (MetaAPI returns candles backwards from here).
          // Preserve the server date string as-is (no UTC conversion) to avoid timezone shift.
          const startTime = currentDate
            ? (currentDate.includes(' ') ? currentDate.replace(' ', 'T') : currentDate)
            : now.toISOString();
          onTotal?.(missing);
          // Paginate from the anchor backwards until the oldest candle fetched reaches fromDate.
          // The first page asks for only `missing` candles when the gap fits in one page, but the
          // walk continues either way: a single unverified request used to declare the gap closed
          // even when MetaApi returned fewer candles than asked, silently leaving it open.
          // Track how many candles this gap-fill has already pulled so any continuation page also
          // asks for just what's left, capped at PAGE. Without this, a page past the first fell
          // back to fetchPage's PAGE=1000 default even for a 1-candle gap (e.g. the anchor's own
          // still-forming candle not yet satisfying reachedFloor), turning a 1-row fetch into a
          // 1000-row one on every sync tick.
          let fetchedForGap = 0;
          return fetchPage(startTime, Math.min(missing, PAGE)).pipe(
            expand(state => {
              fetchedForGap += state.candles.length;
              if (state.done) return EMPTY;
              const remaining = Math.min(Math.max(missing - fetchedForGap, 1), PAGE);
              return timer(300).pipe(concatMap(() => fetchPage(state.cursor, remaining)));
            }),
            filter(state => state.candles.length > 0),
            map(state => state.candles),
            tap(candles => { batchIndex++; totalCandles += candles.length; onBatch?.(batchIndex, totalCandles); })
          );
        }
      } catch (e) {
        console.warn('[MetaApiService] getCandlesHistorical date parse failed, falling back to full pagination', e);
      }
    }

    // Default behaviour (no lastCandleDate / initial full download):
    // Use currentDate as anchor if available, otherwise MetaAPI defaults to now.
    const nowMs = currentDate ? new Date(currentDate.includes(' ') ? currentDate.replace(' ', 'T') : currentDate).getTime() : Date.now();
    onTotal?.(Math.ceil((nowMs - fromDate.getTime()) / tfMs));
    const defaultCursor = currentDate
      ? (currentDate.includes(' ') ? currentDate.replace(' ', 'T') : currentDate)
      : null;
    return fetchPage(defaultCursor).pipe(
      expand(state =>
        state.done ? EMPTY : timer(300).pipe(concatMap(() => fetchPage(state.cursor)))
      ),
      filter(state => state.candles.length > 0),
      map(state => state.candles),
      tap(candles => { batchIndex++; totalCandles += candles.length; onBatch?.(batchIndex, totalCandles); })
    );
  }

  /**
   * Últimas `limit` velas (incluida la que está en formación) en una sola petición, sin
   * paginar: sin startTime MetaAPI responde hacia atrás desde ahora. Pensado para refrescar
   * periódicamente una gráfica intradía. Devuelve las velas en orden ascendente.
   */
  getLatestCandles(accountId: string, symbol: string, timeframe: string, limit: number = 3): Observable<any[]> {
    if (!this.hasToken() || !accountId) return of([]);
    const query = '?' + new URLSearchParams({ limit: String(limit) }).toString();
    return this.http.get<any[]>(
      this.urlData(`/users/current/accounts/${accountId}/historical-market-data/symbols/${encodeURIComponent(symbol)}/timeframes/${encodeURIComponent(timeframe)}/candles${query}`),
      { headers: this.headers() }
    ).pipe(
      map((raw: any[]) => (raw ?? [])
        .map(c => ({ c, t: this.candleComparisonTime(c, timeframe) }))
        .filter((item): item is { c: any; t: number } => item.t != null)
        .sort((a, b) => a.t - b.t)
        .map(i => i.c))
    );
  }

  /**
   * Resumes a possibly-interrupted download using the extended `tieneCandles` response
   * (ultimaFecha/fechaActual + primeraFecha + huecoInferior/huecoSuperior).
   * Runs up to three fill passes in sequence, each reusing getCandlesHistorical with a
   * different anchor — self-healing: any pass can be interrupted again and the next call
   * to tieneCandles will re-detect whatever is still missing, no session/progress tracking needed.
   *   1) internal gap  (huecoInferior/huecoSuperior)      — closes a hole in the middle of the series
   *   2) old-tail       (primeraFecha vs historicalFromDate) — extends an incomplete initial backfill
   *   3) recent-tail     (ultimaFecha vs fechaActual)         — existing day-to-day gap-fill
   * Emits one array per batch, same contract as getCandlesHistorical.
   */
  downloadMissingCandles(
    accountId: string,
    symbol: string,
    timeframe: string,
    res: { tieneCandles: boolean; ultimaFecha?: string; fechaActual?: string; primeraFecha?: string; huecoInferior?: string; huecoSuperior?: string; huecos?: { inferior: string; superior: string }[]; historiaAgotadaDesde?: string },
    onBatch?: (batchIndex: number, totalCandles: number) => void,
    onTotal?: (expectedCandles: number) => void,
    onFase?: (fase: MetaApiDownloadFase) => void,
    onExhausted?: (earliestConfirmedDate: string) => void,
    // Called once a gap-fill pass finishes without finding any real candle strictly inside
    // [inferior, superior] — signals the gap doesn't exist at the source, not just a pagination
    // artifact. Distinct from onExhausted, which only marks the old-tail floor.
    onHuecoExhausted?: (inferior: string, superior: string) => void
  ): Observable<any[]> {
    const tfMs = this.timeframeMs(timeframe);
    const parse = (v?: string) => this.parseFechaServidor(v);
    const passes: Observable<any[]>[] = [];

    // Pass 1: close every internal gap the backend detected, newest first. One pass per gap —
    // the backend used to report only the first one, so closing a series with several holes took
    // as many sync cycles as it had holes.
    const huecos = res.huecos?.length
      ? res.huecos
      : (res.huecoInferior && res.huecoSuperior
          ? [{ inferior: res.huecoInferior, superior: res.huecoSuperior }]
          : []);
    for (const hueco of huecos) {
      const huecoInf = parse(hueco.inferior);
      const huecoSup = parse(hueco.superior);
      if (!huecoInf || !hueco.superior || !huecoSup) continue;
      const fromDate = new Date(huecoInf.getTime() + tfMs);
      // defer() delays the onFase signal until concat() actually subscribes to this pass,
      // so the label only flips once the previous pass has finished.
      passes.push(defer(() => {
        onFase?.('hueco');
        let foundInside = false;
        // When the source genuinely has nothing in this range, MetaApi doesn't answer with an
        // empty page — it just skips straight to the next real candle before `hueco.inferior`,
        // which satisfies the pagination floor without ever hitting the onExhausted branch. So
        // this pass has to detect "found nothing inside the gap" itself instead of relying on it.
        return this.getCandlesHistorical(accountId, symbol, timeframe, fromDate, hueco.inferior, hueco.superior, onBatch, onTotal).pipe(
          tap({
            next: (batch: any[]) => {
              if (foundInside || !batch) return;
              if (batch.some(c => {
                const t = this.candleComparisonTime(c, timeframe);
                return t != null && t > huecoInf.getTime() && t < huecoSup.getTime();
              })) foundInside = true;
            },
            complete: () => { if (!foundInside) onHuecoExhausted?.(hueco.inferior, hueco.superior); }
          })
        );
      }));
    }

    // Pass 2: extend an incomplete initial backfill back to the intended historical floor.
    const primeraFecha = parse(res.primeraFecha);
    if (primeraFecha) {
      const theoFloor = MetaApiService.historicalFromDate(timeframe);
      // If MetaApi already confirmed it has nothing before historiaAgotadaDesde, use that as
      // the real floor instead of the theoretical one, so we stop re-requesting a range we
      // already know is empty.
      const historiaAgotadaDesde = parse(res.historiaAgotadaDesde);
      const floor = historiaAgotadaDesde && historiaAgotadaDesde.getTime() > theoFloor.getTime()
        ? historiaAgotadaDesde : theoFloor;
      if (primeraFecha.getTime() - floor.getTime() > tfMs) {
        passes.push(defer(() => {
          onFase?.('cola-antigua');
          return this.getCandlesHistorical(accountId, symbol, timeframe, floor, floor.toISOString(), res.primeraFecha, onBatch, onTotal, onExhausted);
        }));
      }
    }

    // Pass 3: existing day-to-day gap-fill (now vs ultimaFecha).
    const ultimaFecha = parse(res.ultimaFecha);
    const fechaActual = parse(res.fechaActual);
    if (ultimaFecha && fechaActual && fechaActual.getTime() - ultimaFecha.getTime() >= tfMs) {
      const fromDate = new Date(ultimaFecha.getTime() + tfMs);
      passes.push(defer(() => {
        onFase?.('reciente');
        return this.getCandlesHistorical(accountId, symbol, timeframe, fromDate, res.ultimaFecha, res.fechaActual, onBatch, onTotal);
      }));
    }

    return passes.length ? concat(...passes) : of([]);
  }

  /**
   * Same convention as candleComparisonTime: brokerTime-style strings ("yyyy-MM-dd HH:mm:ss")
   * carry no timezone and must be forced to UTC, or the browser's local offset shifts the gap
   * boundary — a boundary candle that sits exactly on the edge of a real gap can then look like
   * it falls inside it, which silently blocks onHuecoExhausted forever (the gap never gets
   * confirmed and keeps being re-requested on every sync). Bare dates ("yyyy-MM-dd", the daily
   * period) have no time component and are already UTC per the Date spec, so they're left as-is.
   */
  private parseFechaServidor(v?: string): Date | null {
    if (!v) return null;
    const hasTime = v.includes(' ') || v.includes('T');
    const normalized = v.includes(' ') ? v.replace(' ', 'T') : v;
    const d = new Date(hasTime ? normalized + 'Z' : normalized);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  /**
   * Las últimas `n` barras reales del símbolo, más el tramo que las separe de lo ya guardado.
   *
   * Cuenta barras, no tiempo de reloj: sin startTime MetaAPI responde hacia atrás desde ahora y
   * solo devuelve las barras que existen, así que en la apertura las `n` incluyen las de la sesión
   * anterior. Por eso no se usa `(fechaActual - ultimaFecha) / tf`, que un lunes en 1m mediría el
   * fin de semana cerrado (~2900 "faltantes") y pediría páginas del viernes ya guardadas.
   *
   * Primero pide `n + 1` (la vela en formación la descarta el backend). Si la barra más antigua
   * recibida no enlaza con `ultimaFecha`, sigue paginando hacia atrás hasta llegar a ella. Emite un
   * array por lote, igual que getCandlesHistorical.
   */
  downloadUltimasBarras(accountId: string, symbol: string, timeframe: string, ultimaFecha?: string, n: number = 200): Observable<any[]> {
    const tfMs = this.timeframeMs(timeframe);
    const guardada = this.parseFechaServidor(ultimaFecha);
    return this.getLatestCandles(accountId, symbol, timeframe, n + 1).pipe(
      concatMap(primera => {
        const tiempos = primera
          .map(c => this.candleComparisonTime(c, timeframe))
          .filter((t): t is number => t != null);
        const pagina$ = of(primera);
        if (!guardada || tiempos.length === 0) return pagina$;
        const masAntigua = Math.min(...tiempos);
        if (masAntigua - tfMs <= guardada.getTime()) return pagina$;
        // Entre lo guardado y la ventana de n barras hay un tramo sin descargar: se recorre hacia
        // atrás desde la barra anterior a la más antigua hasta alcanzar ultimaFecha.
        const masAntiguaReal = Math.min(...primera.map(c => this.candleRealTime(c) ?? this.candleComparisonTime(c, timeframe) ?? masAntigua));
        const cursor = new Date(masAntiguaReal - tfMs).toISOString();
        const desde = new Date(guardada.getTime() + tfMs);
        return concat(pagina$, timer(300).pipe(
          concatMap(() => this.getCandlesHistorical(accountId, symbol, timeframe, desde, undefined, cursor))
        ));
      }),
      filter(candles => candles.length > 0)
    );
  }

  /**
   * Connectivity check — equivalent to IBKR /iserver/auth/status.
   * MetaAPI has no dedicated status endpoint; a successful response from
   * /accountInformation means the token is valid and the terminal is connected.
   */
  // ---------------------------------------------------------------------------
  // Token auto-refresh
  // ---------------------------------------------------------------------------

  private doRefreshToken(accountId: string): Observable<void> {
    const licencia = this.cookieService.get('auth_token');
    return this.http.post<{ token: string }>(
      `${this.backendUrl}metaapi/refresh-token/${accountId}`,
      {},
      { headers: new HttpHeaders({ 'licencia': licencia, 'Content-Type': 'application/json' }) }
    ).pipe(
      tap(res => {
        this.saveCredentials(res.token, accountId);
        this.toastr.info('Sesión MetaAPI renovada automáticamente', 'Reconexión', { timeOut: 4000 });
      }),
      map(() => void 0 as void),
      catchError(err => {
        this.toastr.error(
          'No se pudo renovar la sesión MetaAPI. Reconecta tu cuenta de broker.',
          'Sesión expirada',
          { timeOut: 8000 }
        );
        return throwError(() => err);
      })
    );
  }

  /**
   * @param rethrow optional predicate: matching errors are propagated instead
   *                of being replaced by `fallback`.
   */
  private callWithAutoRefresh<T>(
    accountId: string,
    request: () => Observable<T>,
    fallback: T,
    rethrow?: (err: HttpErrorResponse) => boolean
  ): Observable<T> {
    // No token (logged out): the 401 would trigger a refresh that restores the cleared credentials
    if (!this.hasToken()) return of(fallback);
    return request().pipe(
      catchError((err: HttpErrorResponse) => {
        if (rethrow?.(err)) {
          return throwError(() => err);
        }
        // The local gateway has no token to renew
        if (err.status === 401 && !this.refreshing && !this.isGateway()) {
          this.refreshing = true;
          return this.doRefreshToken(accountId).pipe(
            switchMap(() => {
              this.refreshing = false;
              return request();
            }),
            catchError(() => {
              this.refreshing = false;
              return of(fallback);
            })
          );
        }
        return of(fallback);
      })
    );
  }

  /**
   * @param maxAgeMs max age of the cross-tab cached response to accept;
   *                 0 forces a real request with strict validation (login / deploy flows).
   *
   * With maxAgeMs > 0, up to MAX_TRANSIENT_FAILURES consecutive transient
   * failures (504, 5xx, network, timeout) report the last known state instead
   * of "disconnected", so a single MetaAPI hiccup doesn't disconnect the app.
   */
  verifyConnection(accountId: string, maxAgeMs: number = ACC_INFO_TTL_MS): Observable<boolean> {
    const info$ = maxAgeMs > 0
      ? this.fetchAccountInformationShared(accountId, maxAgeMs)
      : this.getAccountInformation(accountId);
    return new Observable(observer => {
      const emit = (ok: boolean) => {
        observer.next(ok);
        observer.complete();
      };
      info$.subscribe({
        next: (data: any) => {
          const ok = data != null && !data.error;
          this.consecutiveTransientFailures = 0;
          this.lastKnownConnected = ok;
          this.connectedSubject.next(ok);
          emit(ok);
        },
        error: (err: any) => {
          if (this.isTransientError(err) && ++this.consecutiveTransientFailures < MAX_TRANSIENT_FAILURES) {
            // Not verified in this tab yet: fall back to the app-wide persisted state
            emit(this.lastKnownConnected ?? localStorage.getItem('brokerConectado') === 'true');
            return;
          }
          this.lastKnownConnected = false;
          this.connectedSubject.next(false);
          emit(false);
        }
      });
    });
  }
}
